import { GRAY_1000, WHITE } from 'constants/styling/theme'

import Box from '@mui/material/Box'
import { FC } from 'react'

/**
 * @interface Props Input properties
 */
export interface Props {
  /** The tag's text, translated by the caller (e.g. "AI", "KI" in German) */
  label: string
  /** `overlay` (default): in the bottom-right corner of its positioned parent, a thumbnail; `inline`: in a line of text */
  variant?: 'overlay' | 'inline'
  /** Hidden from assistive technology, e.g. when the thumbnail's name already says it was made with AI */
  'aria-hidden'?: boolean
}

/**
 * @component A small dark tag saying a visual was made with AI: a video stays a video whatever its origin, only this
 * tag tells. Plain text, read with the thumbnail or the line it sits in.
 * @example
 * <span style={{ position: 'relative' }}><MediaThumb src={url} alt="" /><AiFlag label={t('ai_flag')} aria-hidden /></span>
 */
export const AiFlag: FC<Props> = ({
  label,
  variant = 'overlay',
  'aria-hidden': ariaHidden,
}) => (
  <Box
    component="span"
    aria-hidden={ariaHidden || undefined}
    sx={{
      ...(variant === 'overlay'
        ? { position: 'absolute', right: '.4rem', bottom: '.4rem', zIndex: 1 }
        : { position: 'static', display: 'inline-block', verticalAlign: '1px' }),
      padding: '0 .4rem',
      borderRadius: '.4rem',
      backgroundColor: GRAY_1000,
      color: WHITE,
      fontSize: '1.2rem',
      lineHeight: '1.8rem',
      fontWeight: 550,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
    }}
  >
    {label}
  </Box>
)
